/**
 * Sprint 7a — digital pre-shift checklist (ADR 0013, ADR 0016).
 *
 * The database is the source of truth (supabase/migrations/20270222000001_checklists.sql); the rules
 * below are the SAME rules in pure TypeScript so the form, the console and the Worker can explain and
 * pre-check them, and database tests check both agree:
 *
 *   * templates: five system templates chosen by vehicle category; a tenant may copy one, edit it and
 *     assign it to a category instead of the system template;
 *   * answers ok / not_ok / na ("Yok"), a note is required on not_ok, a photo is required on not_ok for
 *     items flagged photo_required_on_fail; result ok / ok_with_notes / maintenance_required;
 *   * shifts: name, local start and end (end before start = overnight), ISO weekdays of the START day,
 *     IANA time zone; DST is handled by the wall clock (a 22:00–06:00 shift is 7 or 9 hours long on a
 *     DST night).
 *
 * ADR 0003 / ADR 0013: a checklist result never locks or commands a vehicle, and the operator is never
 * shown a warning; a critical failure only notifies the people chosen for the site.
 */
import type { PowerType, Site, VehicleType } from "./domain.js";
import { localParts, parseHm, zonedTimeToUtc } from "./alarms.js";
import type { KysFormRef } from "./kys.js";

/* ---------------- categories and system templates ---------------- */

export const CHECKLIST_CATEGORIES = ["counterbalance_electric", "counterbalance_ic", "reach_truck", "order_picker", "pallet_truck"] as const;
export type ChecklistCategory = (typeof CHECKLIST_CATEGORIES)[number];

/**
 * Template category of a vehicle (SQL: fts_checklist_category). Counterbalance forklifts split by power:
 * electric -> counterbalance_electric, diesel/LPG -> counterbalance_ic. "other" vehicles use the
 * counterbalance template of their power type.
 */
export function checklistCategory(type: VehicleType, power: PowerType): ChecklistCategory {
  if (type === "reach_truck" || type === "order_picker" || type === "pallet_truck") return type;
  return power === "electric" ? "counterbalance_electric" : "counterbalance_ic";
}

export const CHECKLIST_ANSWERS = ["ok", "not_ok", "na"] as const;
export type ChecklistAnswer = (typeof CHECKLIST_ANSWERS)[number];

export const CHECKLIST_RESULTS = ["ok", "ok_with_notes", "maintenance_required"] as const;
export type ChecklistResult = (typeof CHECKLIST_RESULTS)[number];

export const CHECKLIST_LIMITS = Object.freeze({
  noteMax: 500,
  itemTextMax: 200,
  templateNameMax: 80,
  itemsMax: 60,
  photosPerAnswer: 3,
  photoMaxBytes: 5 * 1024 * 1024,
  /** A submission counts for a shift from this many minutes before its start. */
  shiftLeadMin: 30,
  /** checklist_missed is raised this long after the first use in a shift without a checklist. */
  missedGraceMin: 15,
  shiftsPerSite: 10,
  shiftNameMax: 40,
  recipientsPerSite: 20,
  resolutionNoteMax: 500,
});

export const CHECKLIST_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ChecklistPhotoType = (typeof CHECKLIST_PHOTO_TYPES)[number];

/** Item codes: 1–8 characters, upper-case letters, digits and dashes (e.g. "3", "A1", "L2"). */
export const CHECKLIST_ITEM_CODE = /^[A-Z0-9][A-Z0-9-]{0,7}$/;

/** One item of a template. Texts are TR (source) and EN; `textEn` may be empty (TR is shown instead). */
export type ChecklistItemDef = {
  code: string;
  textTr: string;
  textEn: string;
  critical: boolean;
  photoRequiredOnFail: boolean;
  /** Empty = every power type; e.g. ["lpg"] for LPG cylinder items. */
  powerTypes: PowerType[];
  /** Shown first when the vehicle had an impact alarm since its last checklist (body damage). */
  impactFirst: boolean;
  /**
   * "Yok" (N/A) is allowed on a CRITICAL item only with this flag (conditional equipment such as a seat
   * belt or a camera "if fitted"). Non-critical items always allow N/A.
   */
  allowNa: boolean;
};

export type ChecklistItem = ChecklistItemDef & { id: string; templateId: string; position: number };

export type ChecklistTemplate = {
  id: string;
  /** null = system template (read-only, available to every tenant). */
  tenantId: string | null;
  /** System templates only: the category they are for. */
  systemCategory: ChecklistCategory | null;
  name: string;
  /** Tenant templates: the template they were copied from. */
  sourceTemplateId: string | null;
  /** KYS form identity (ADR 0033): code, revision and title printed on the form outputs; absent/null = not a KYS form. */
  form?: KysFormRef | null;
  items: ChecklistItem[];
  updatedAt: string;
};

/** A tenant's choice of template for a category (absent = the system template of that category). */
export type ChecklistAssignment = { tenantId: string; category: ChecklistCategory; templateId: string };

const it = (code: string, textTr: string, textEn: string, critical: boolean, photoRequiredOnFail: boolean, extra: Partial<ChecklistItemDef> = {}): ChecklistItemDef => ({
  code,
  textTr,
  textEn,
  critical,
  photoRequiredOnFail,
  powerTypes: [],
  impactFirst: false,
  // Conditional items ("(varsa)" = if fitted) may be answered N/A even when critical.
  allowNa: /\(varsa\)/.test(textTr),
  ...extra,
});

/** Common items of every template, visual checks first (product owner, 2026-10-18). */
const COMMON: ChecklistItemDef[] = [
  it("1", 'Araçta "Servis dışı" etiketi yok, önceki vardiyanın bildirdiği arıza giderilmiş', 'No "Out of service" tag on the vehicle; faults reported by the previous shift have been fixed', true, false),
  it("2", "Aracın altında yağ, hidrolik sıvı veya akü suyu birikintisi yok", "No oil, hydraulic fluid or battery water puddle under the vehicle", true, false),
  it("3", "Lastik ve tekerleklerde kesik, kopma, aşırı aşınma yok; bijonlar tam", "Tyres and wheels free of cuts, chunking or excessive wear; no wheel nuts missing", true, true),
  it("4", "Çatallarda çatlak, eğilme, topukta aşınma yok; çatal kilit pimleri yerinde", "Forks free of cracks, bends and heel wear; fork locking pins in place", true, true),
  it("5", "Direk zincirlerinde kopuk veya gevşek bakla yok; makaralar ve stoplar sağlam", "Mast chains have no broken or loose links; rollers and stops intact", true, true),
  it("6", "Hidrolik hortumlarda çatlak, şişme veya sızıntı yok", "Hydraulic hoses free of cracks, bulges and leaks", true, true),
  it("7", "Operatör tavanı (üst koruma) ve yük sırt dayanağı sağlam, eğilmemiş", "Overhead guard and load backrest intact, not bent", true, true),
  it("8", "Kapasite etiketi ve uyarı etiketleri yerinde ve okunuyor", "Capacity plate and warning labels in place and legible", false, false),
  it("9", "Aynalar ve camlar sağlam ve temiz", "Mirrors and windows intact and clean", false, true),
  it("17", "Gövde, kabin ve koruyucularda yeni çarpma izi, ezik veya kırık yok", "No new impact marks, dents or breaks on the body, cab or guards", false, true, { impactFirst: true }),
  it("10", "Koltuk sağlam, emniyet kemeri takılıp kilitleniyor", "Seat intact; seat belt buckles and locks", true, false, { allowNa: true }),
  it("11", "Gösterge panelinde uyarı lambası veya arıza kodu yok", "No warning light or fault code on the instrument panel", false, false),
  it("12", "Korna çalışıyor", "Horn works", true, false),
  it("13", "Far, tepe lambası, geri vites ikazı ve (varsa) mavi/kırmızı zemin ışığı çalışıyor", "Headlights, beacon, reversing alarm and (if fitted) blue/red floor light work", false, false),
  it("14", "Servis freni aracı düzgün durduruyor; park freni aracı tutuyor", "Service brake stops the vehicle smoothly; parking brake holds it", true, false),
  it("15", "Direksiyon iki yöne normal dönüyor; boşluk veya sertlik yok", "Steering turns normally both ways, without play or stiffness", true, false),
  it("16", "Kaldırma, indirme, devirme ve (varsa) yan kaydırma ile ataşman düzgün çalışıyor; direk kendiliğinden inmiyor", "Lift, lower, tilt and (if fitted) side shift and attachment work smoothly; the mast does not drift down", true, false, { allowNa: false }),
];

const A1 = it("A1", "Akü soketi ve kablolarında ezik, yanık veya açıkta tel yok", "Battery connector and cables free of crush damage, burn marks and exposed wire", true, true);
const A2 = it("A2", "Akü yerinde sabit, tutucu/kilit ve akü kapağı kapalı", "Battery secured in place; retainer/lock and battery cover closed", true, false);
// Hook (ADR 0016): A3 can later be pre-filled from the battery state of charge (ADR 0012); the operator still confirms it.
const A3 = it("A3", "Akü göstergesi vardiyaya yetecek seviyede", "Battery gauge shows enough charge for the shift", false, false);
const A4 = it("A4", "Akü su seviyesi normal (yalnız kurşun-asit akü)", "Battery water level normal (lead-acid batteries only)", false, false, { allowNa: true });
const A5 = it("A5", "Acil stop butonu aracı durduruyor", "Emergency stop button stops the vehicle", true, false);

const LPG: ChecklistItemDef[] = [
  it("L1", "LPG tüpü yatağına oturmuş, kayışı/kilidi kapalı, tüp üzerinde ezik yok", "LPG cylinder seated in its cradle, strap/lock closed, no dents on the cylinder", true, true, { powerTypes: ["lpg"], allowNa: true }),
  it("L2", "LPG hortum ve bağlantılarında gaz kokusu, buzlanma veya hasar yok", "No gas smell, frosting or damage on LPG hoses and fittings", true, false, { powerTypes: ["lpg"], allowNa: true }),
];
const DIESEL: ChecklistItemDef[] = [
  it("D1", "Motor yağı ve soğutma suyu seviyesi normal", "Engine oil and coolant levels normal", false, false),
  it("D2", "Motor kayışları sağlam, motor kaputu kilitli", "Engine belts intact; engine hood locked", false, false),
  it("D3", "Egzozda aşırı duman yok, motordan anormal ses gelmiyor", "No excessive exhaust smoke and no abnormal engine noise", false, false),
];
const REACH: ChecklistItemDef[] = [
  it("R1", "Ölü adam pedalı bırakılınca araç duruyor", "The vehicle stops when the dead-man pedal is released", true, false),
  it("R2", "İleri-geri uzanma (reach) hareketi düzgün, raylarda engel yok", "Reach movement in and out is smooth; nothing obstructs the rails", true, false),
  it("R3", "Destek ayakları ve yük tekerlekleri sağlam, tekerleklerde çatlak yok", "Straddle legs and load wheels intact; no cracked wheels", true, true),
  it("R4", "Direk kamerası veya yükseklik göstergesi (varsa) çalışıyor", "Mast camera or height indicator (if fitted) works", false, false),
];
const PICKER: ChecklistItemDef[] = [
  it("O1", "Platform kapıları kapanıyor; kapı açıkken platform hareket etmiyor", "Platform gates close; the platform does not move while a gate is open", true, false),
  it("O2", "Emniyet kemeri veya yaşam halatı ve bağlantı noktası sağlam", "Harness or lanyard and its anchor point intact", true, true),
  it("O3", "Ölü adam pedalı ve iki el kumandası çalışıyor", "Dead-man pedal and two-hand controls work", true, false),
  it("O4", "Acil indirme vanası yerinde ve erişilebilir", "Emergency lowering valve in place and accessible", true, false),
  it("O5", "Ray kılavuz tekerlekleri (varsa) sağlam", "Rail guide rollers (if fitted) intact", false, false),
];
const PALLET: ChecklistItemDef[] = [
  it("T1", "Timon bırakılınca fren devreye giriyor", "The brake engages when the tiller is released", true, false),
  it("T2", "Göbek butonu (geri itme emniyeti) aracı geri itiyor", "Belly button (reverse safety) drives the truck back", true, false),
  it("T3", "Ayak koruma ve kılavuz tekerlekleri sağlam", "Foot guard and guide rollers intact", false, false),
];

export type SystemChecklistTemplate = { category: ChecklistCategory; nameTr: string; nameEn: string; items: ChecklistItemDef[] };

/** The five standard templates, seeded by the migration with exactly these items (a test checks parity). */
export const SYSTEM_CHECKLIST_TEMPLATES: readonly SystemChecklistTemplate[] = Object.freeze([
  { category: "counterbalance_electric", nameTr: "Akülü forklift (standart)", nameEn: "Electric counterbalance (standard)", items: [...COMMON, A1, A2, A3, A4, A5] },
  { category: "counterbalance_ic", nameTr: "Dizel/LPG forklift (standart)", nameEn: "Diesel/LPG counterbalance (standard)", items: [...COMMON, ...LPG, ...DIESEL] },
  { category: "reach_truck", nameTr: "Reach truck (standart)", nameEn: "Reach truck (standard)", items: [...COMMON, A1, A2, A3, A5, ...REACH] },
  { category: "order_picker", nameTr: "Sipariş toplama aracı (standart)", nameEn: "Order picker (standard)", items: [...COMMON, A1, A2, A3, A5, ...PICKER] },
  { category: "pallet_truck", nameTr: "Akülü transpalet (standart)", nameEn: "Electric pallet truck (standard)", items: [...COMMON, A1, A2, A3, A5, ...PALLET] },
]);

/** Template that applies to a category: the tenant's assignment, else the system template. */
export function effectiveChecklistTemplate(
  category: ChecklistCategory,
  assignments: Pick<ChecklistAssignment, "category" | "templateId">[],
  templates: Pick<ChecklistTemplate, "id" | "systemCategory">[],
): string | null {
  const a = assignments.find((x) => x.category === category);
  if (a && templates.some((t) => t.id === a.templateId)) return a.templateId;
  return templates.find((t) => t.systemCategory === category)?.id ?? null;
}

/** Items shown for a vehicle of this power type, in form order (impact-first items first after an impact). */
export function formItems<T extends Pick<ChecklistItemDef, "powerTypes" | "impactFirst"> & { position: number }>(
  items: T[],
  power: PowerType,
  impactSinceLast: boolean,
): T[] {
  const list = items.filter((i) => i.powerTypes.length === 0 || i.powerTypes.includes(power)).sort((a, b) => a.position - b.position);
  if (!impactSinceLast) return list;
  return [...list.filter((i) => i.impactFirst), ...list.filter((i) => !i.impactFirst)];
}

/* ---------------- answers, validation and result ---------------- */

export type ChecklistAnswerInput = { itemId: string; answer: ChecklistAnswer; note?: string | null; photoIds?: string[] };

export const CHECKLIST_ISSUES = [
  "missing_answer",
  "duplicate_answer",
  "unknown_item",
  "invalid_answer",
  "note_required",
  "note_too_long",
  "photo_required",
  "too_many_photos",
  // A critical item without allow_na answered "Yok" (ADR 0016).
  "na_not_allowed",
] as const;
export type ChecklistIssueCode = (typeof CHECKLIST_ISSUES)[number];
export type ChecklistIssue = { itemId: string | null; code: ChecklistIssueCode };

/**
 * `naAllowed` (as the public form receives it) overrides `critical`/`allowNa` when given; otherwise N/A is
 * refused on a critical item without allowNa.
 */
type RuleItem = Pick<ChecklistItemDef, "critical" | "photoRequiredOnFail"> & { id: string; allowNa?: boolean; naAllowed?: boolean };

/** May this item be answered "Yok"? (SQL: not (critical and not allow_na)). */
export const naAllowed = (i: Pick<RuleItem, "critical" | "allowNa" | "naAllowed">): boolean => i.naAllowed ?? !(i.critical && !i.allowNa);

const trimmed = (s: string | null | undefined) => (s ?? "").trim();

/**
 * Same checks as the database (checklist_submit): every item answered exactly once with ok / not_ok /
 * na; not_ok needs a note; not_ok on a photo_required_on_fail item needs at least one photo; at most
 * 3 photos per answer; notes ≤ 500 characters. Photos are optional on every other answer.
 */
export function validateChecklistAnswers(items: RuleItem[], answers: ChecklistAnswerInput[]): ChecklistIssue[] {
  const issues: ChecklistIssue[] = [];
  const known = new Map(items.map((i) => [i.id, i]));
  const seen = new Set<string>();
  for (const a of answers) {
    const item = known.get(a.itemId);
    if (!item) {
      issues.push({ itemId: a.itemId, code: "unknown_item" });
      continue;
    }
    if (seen.has(a.itemId)) {
      issues.push({ itemId: a.itemId, code: "duplicate_answer" });
      continue;
    }
    seen.add(a.itemId);
    if (!(CHECKLIST_ANSWERS as readonly string[]).includes(a.answer)) {
      issues.push({ itemId: a.itemId, code: "invalid_answer" });
      continue;
    }
    const note = trimmed(a.note);
    const photos = a.photoIds ?? [];
    if ([...note].length > CHECKLIST_LIMITS.noteMax) issues.push({ itemId: a.itemId, code: "note_too_long" });
    if (photos.length > CHECKLIST_LIMITS.photosPerAnswer) issues.push({ itemId: a.itemId, code: "too_many_photos" });
    if (a.answer === "na" && !naAllowed(item)) issues.push({ itemId: a.itemId, code: "na_not_allowed" });
    if (a.answer === "not_ok") {
      if (!note) issues.push({ itemId: a.itemId, code: "note_required" });
      if (item.photoRequiredOnFail && photos.length === 0) issues.push({ itemId: a.itemId, code: "photo_required" });
    }
  }
  for (const i of items) if (!seen.has(i.id)) issues.push({ itemId: i.id, code: "missing_answer" });
  return issues;
}

/** ok · ok_with_notes (a non-critical not_ok) · maintenance_required (any critical not_ok). */
export function checklistResult(items: Pick<RuleItem, "id" | "critical">[], answers: Pick<ChecklistAnswerInput, "itemId" | "answer">[]): ChecklistResult {
  const critical = new Map(items.map((i) => [i.id, i.critical]));
  const fails = answers.filter((a) => a.answer === "not_ok");
  if (fails.some((a) => critical.get(a.itemId) === true)) return "maintenance_required";
  return fails.length > 0 ? "ok_with_notes" : "ok";
}

/** Alarm raised for a submission (none for "ok"). Non-critical faults go to the daily digest (ADR 0016). */
export function checklistAlarmType(result: ChecklistResult): "checklist_critical" | "checklist_notes" | null {
  return result === "maintenance_required" ? "checklist_critical" : result === "ok_with_notes" ? "checklist_notes" : null;
}

/* ---------------- template editing ---------------- */

export const TEMPLATE_ISSUES = ["name_required", "name_too_long", "no_items", "too_many_items", "text_required", "text_too_long", "invalid_code", "duplicate_code"] as const;
export type TemplateIssueCode = (typeof TEMPLATE_ISSUES)[number];
export type TemplateIssue = { index: number | null; code: TemplateIssueCode };

/** Same rules as save_checklist_template(). `index` is the item's position in the list (null = the template). */
export function validateChecklistTemplate(name: string, items: Pick<ChecklistItemDef, "code" | "textTr" | "textEn">[]): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const n = name.trim();
  if (!n) issues.push({ index: null, code: "name_required" });
  else if ([...n].length > CHECKLIST_LIMITS.templateNameMax) issues.push({ index: null, code: "name_too_long" });
  if (items.length === 0) issues.push({ index: null, code: "no_items" });
  if (items.length > CHECKLIST_LIMITS.itemsMax) issues.push({ index: null, code: "too_many_items" });
  const codes = new Set<string>();
  items.forEach((i, index) => {
    const code = i.code.trim().toUpperCase();
    if (!CHECKLIST_ITEM_CODE.test(code)) issues.push({ index, code: "invalid_code" });
    else if (codes.has(code)) issues.push({ index, code: "duplicate_code" });
    codes.add(code);
    if (!i.textTr.trim()) issues.push({ index, code: "text_required" });
    if ([...i.textTr.trim()].length > CHECKLIST_LIMITS.itemTextMax || [...i.textEn.trim()].length > CHECKLIST_LIMITS.itemTextMax) {
      issues.push({ index, code: "text_too_long" });
    }
  });
  return issues;
}

/* ---------------- shifts ---------------- */

/** A site's shift. Times are "HH:MM" local to `tz`; `end` <= `start` means the shift ends the next day. */
export type ShiftDef = { id: string; name: string; start: string; end: string; weekdays: number[]; tz: string };
export type SiteShift = ShiftDef & { tenantId: string; siteId: string };

/** One occurrence of a shift: [startsAt, endsAt). */
export type ShiftInstance = { shiftId: string; name: string; startsAt: Date; endsAt: Date };

/** Same as the guard trigger: name 1–40, valid times, start ≠ end, at least one weekday in 1..7. */
export function validShift(s: Pick<ShiftDef, "name" | "start" | "end" | "weekdays">): boolean {
  const start = parseHm(s.start);
  const end = parseHm(s.end);
  const name = s.name.trim();
  return (
    name.length > 0 &&
    [...name].length <= CHECKLIST_LIMITS.shiftNameMax &&
    !Number.isNaN(start) &&
    !Number.isNaN(end) &&
    start < 1440 &&
    end < 1440 &&
    start !== end &&
    s.weekdays.length > 0 &&
    s.weekdays.every((d) => Number.isInteger(d) && d >= 1 && d <= 7)
  );
}

export const isOvernightShift = (s: Pick<ShiftDef, "start" | "end">): boolean => parseHm(s.end) < parseHm(s.start);

const DAY_MS = 86_400_000;
function addDays(date: string, n: number): string {
  return new Date(Date.parse(`${date}T12:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}
function isoDay(date: string): number {
  const d = new Date(`${date}T12:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/**
 * Occurrences of the shifts that overlap [from, to), sorted by start (SQL: fts_shift_instances). A shift
 * occurs on each listed weekday of its START day; an overnight shift ends on the next calendar day.
 */
export function shiftInstances(shifts: ShiftDef[], from: Date, to: Date): ShiftInstance[] {
  const out: ShiftInstance[] = [];
  for (const s of shifts) {
    if (!validShift(s)) continue;
    const first = localParts(new Date(from.getTime() - DAY_MS), s.tz).date;
    const last = localParts(to, s.tz).date;
    for (let day = first, n = 0; day <= last && n < 400; day = addDays(day, 1), n++) {
      if (!s.weekdays.includes(isoDay(day))) continue;
      const startsAt = zonedTimeToUtc(day, s.start, s.tz);
      const endsAt = zonedTimeToUtc(isOvernightShift(s) ? addDays(day, 1) : day, s.end, s.tz);
      if (startsAt.getTime() < to.getTime() && endsAt.getTime() > from.getTime()) out.push({ shiftId: s.id, name: s.name, startsAt, endsAt });
    }
  }
  return out.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime() || (a.shiftId < b.shiftId ? -1 : a.shiftId > b.shiftId ? 1 : 0));
}

/**
 * The shift a checklist filled in at `at` counts for (SQL: fts_shift_at): an occurrence with
 * startsAt − lead ≤ at < endsAt; when several match (the next shift's lead overlaps the end of the
 * current one) the latest start wins. null = no shift at that time.
 */
export function shiftAt(shifts: ShiftDef[], at: Date, leadMin: number = CHECKLIST_LIMITS.shiftLeadMin): ShiftInstance | null {
  const lead = leadMin * 60_000;
  const list = shiftInstances(shifts, new Date(at.getTime() - DAY_MS), new Date(at.getTime() + lead + 1));
  let best: ShiftInstance | null = null;
  for (const i of list) {
    if (i.startsAt.getTime() - lead <= at.getTime() && at.getTime() < i.endsAt.getTime()) {
      if (!best || i.startsAt.getTime() >= best.startsAt.getTime()) best = i;
    }
  }
  return best;
}

type SiteRef = Pick<Site, "id" | "parentId">;
function chain(sites: SiteRef[], siteId: string): string[] {
  const byId = new Map(sites.map((s) => [s.id, s]));
  const out: string[] = [];
  let cur: string | null = siteId;
  while (cur && !out.includes(cur) && out.length < 64) {
    out.push(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return out;
}

/** Shifts of the site, else of the nearest ancestor that has any (SQL: fts_site_shifts). */
export function effectiveShifts<T extends Pick<SiteShift, "siteId">>(sites: SiteRef[], shifts: T[], siteId: string): { shifts: T[]; sourceSiteId: string | null } {
  for (const id of chain(sites, siteId)) {
    const own = shifts.filter((s) => s.siteId === id);
    if (own.length > 0) return { shifts: own, sourceSiteId: id };
  }
  return { shifts: [], sourceSiteId: null };
}

/* ---------------- checklist alert recipients ---------------- */

/** A console member (userId) or an external e-mail address (e.g. an outside maintenance company). */
export type ChecklistRecipient = { id: string; tenantId: string; siteId: string; userId: string | null; email: string | null; label: string | null };

/**
 * Who receives a site's checklist alarms. Members and external addresses resolve SEPARATELY (SQL:
 * fts_checklist_member_level / fts_checklist_external_level), so a site that lists only an outside firm
 * still notifies its staff:
 *   members   = member recipients of the nearest site (itself, then ancestors) with a reachable member
 *               (`memberReachable`: still sees the site and has an e-mail address); none = the day
 *               recipients (ADR 0011);
 *   externals = external addresses of the nearest site that has any; additive.
 */
export function effectiveChecklistRecipients<T extends Pick<ChecklistRecipient, "siteId" | "userId" | "email">>(
  sites: SiteRef[],
  recipients: T[],
  siteId: string,
  memberReachable: (r: T) => boolean = () => true,
): { members: T[]; memberSourceSiteId: string | null; externals: T[]; externalSourceSiteId: string | null } {
  const ids = chain(sites, siteId);
  let members: T[] = [];
  let memberSourceSiteId: string | null = null;
  for (const id of ids) {
    const own = recipients.filter((r) => r.siteId === id && r.userId !== null);
    if (own.some(memberReachable)) {
      members = own.filter(memberReachable);
      memberSourceSiteId = id;
      break;
    }
  }
  let externals: T[] = [];
  let externalSourceSiteId: string | null = null;
  for (const id of ids) {
    const own = recipients.filter((r) => r.siteId === id && r.email !== null);
    if (own.length > 0) {
      externals = own;
      externalSourceSiteId = id;
      break;
    }
  }
  return { members, memberSourceSiteId, externals, externalSourceSiteId };
}

/** Lower-case, trimmed; null when it does not look like an e-mail address (same rule as the guard trigger). */
export function normalizeRecipientEmail(v: string): string | null {
  const e = v.trim().toLowerCase();
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) && e.length <= 254 ? e : null;
}

/* ---------------- photos ---------------- */

/** Content type from the file's first bytes (JPEG, PNG, WebP); null for anything else. */
export function sniffImageType(b: Uint8Array): ChecklistPhotoType | null {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((x, i) => b[i] === x)) return "image/png";
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return "image/webp";
  return null;
}

/** QR token of a vehicle: 32 lower-case hex characters (122 random bits). */
export const CHECKLIST_TOKEN = /^[0-9a-f]{32}$/;

/* ---------------- photo metadata ---------------- */

/**
 * Remove metadata before a photo is stored (location, device, owner): JPEG APP1–APP15 and comment
 * segments (APP0/JFIF stays), PNG tEXt/iTXt/zTXt/eXIf/tIME chunks, WebP EXIF and XMP chunks (and their VP8X
 * flags). Returns the cleaned bytes, or null when the file structure is broken (the photo is refused).
 */
export function stripImageMetadata(b: Uint8Array, type: ChecklistPhotoType): Uint8Array | null {
  try {
    if (type === "image/jpeg") return stripJpeg(b);
    if (type === "image/png") return stripPng(b);
    return stripWebp(b);
  } catch {
    return null;
  }
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function stripJpeg(b: Uint8Array): Uint8Array | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
  const parts: Uint8Array[] = [b.subarray(0, 2)];
  let i = 2;
  while (i < b.length) {
    if (b[i] !== 0xff) return null;
    let m = b[i + 1];
    while (m === 0xff) {
      i++;
      m = b[i + 1];
    }
    if (m === undefined) return null;
    if (m === 0xd9) {
      parts.push(b.subarray(i, i + 2));
      return concat(parts);
    }
    if (m === 0xda) {
      // Start of scan: entropy-coded data follows; the rest of the file is image data.
      parts.push(b.subarray(i));
      return concat(parts);
    }
    if ((m >= 0xd0 && m <= 0xd7) || m === 0x01) {
      parts.push(b.subarray(i, i + 2));
      i += 2;
      continue;
    }
    if (i + 4 > b.length) return null;
    const len = (b[i + 2]! << 8) | b[i + 3]!;
    if (len < 2 || i + 2 + len > b.length) return null;
    const metadata = (m >= 0xe1 && m <= 0xef) || m === 0xfe;
    if (!metadata) parts.push(b.subarray(i, i + 2 + len));
    i += 2 + len;
  }
  return null;
}

const PNG_DROP = new Set(["tEXt", "iTXt", "zTXt", "eXIf", "tIME"]);
function stripPng(b: Uint8Array): Uint8Array | null {
  if (b.length < 8 || sniffImageType(b) !== "image/png") return null;
  const parts: Uint8Array[] = [b.subarray(0, 8)];
  let i = 8;
  while (i + 12 <= b.length) {
    const len = ((b[i]! << 24) >>> 0) + (b[i + 1]! << 16) + (b[i + 2]! << 8) + b[i + 3]!;
    const type = String.fromCharCode(b[i + 4]!, b[i + 5]!, b[i + 6]!, b[i + 7]!);
    const end = i + 12 + len;
    if (end > b.length) return null;
    if (!PNG_DROP.has(type)) parts.push(b.subarray(i, end));
    i = end;
    if (type === "IEND") return concat(parts);
  }
  return null;
}

function stripWebp(b: Uint8Array): Uint8Array | null {
  if (sniffImageType(b) !== "image/webp") return null;
  const parts: Uint8Array[] = [];
  let i = 12;
  while (i + 8 <= b.length) {
    const fourcc = String.fromCharCode(b[i]!, b[i + 1]!, b[i + 2]!, b[i + 3]!);
    const size = b[i + 4]! + (b[i + 5]! << 8) + (b[i + 6]! << 16) + ((b[i + 7]! << 24) >>> 0);
    const end = i + 8 + size + (size % 2);
    if (i + 8 + size > b.length) return null;
    if (fourcc !== "EXIF" && fourcc !== "XMP ") {
      const chunk = b.slice(i, Math.min(end, b.length));
      if (fourcc === "VP8X" && chunk.length > 8) chunk[8] = chunk[8]! & ~0x0c; // clear the EXIF and XMP flags
      parts.push(chunk);
    }
    i = end;
  }
  const body = concat(parts);
  const out = new Uint8Array(12 + body.length);
  out.set(b.subarray(0, 12));
  out.set(body, 12);
  const riff = out.length - 8;
  out[4] = riff & 0xff;
  out[5] = (riff >> 8) & 0xff;
  out[6] = (riff >> 16) & 0xff;
  out[7] = (riff >>> 24) & 0xff;
  return out;
}
